// topoEditor/entities/overlayLink.js — "{ov:5}" becomes an INTERNAL overlay link. In a consumer that wires it,
// clicking makes the referenced overlay visible on the current map and centers on it — NO gallery, NO document
// (overlays are their own entity; an ov reference just points at another overlay). The token stores only the id.
//
// Authoring is via a PICKER (the archive's overlay list), not free text — the picker + its generic {id,title}
// provider (default source: get_overlays) live in the EDITOR wiring, a later step. So the create-flow is not
// invented here: this module is the pure token — parse/serialize/render + matchElement + remove. Mirrors docLink.js.

import { escapeHtml } from "../render.js";

const OVERLAY_PATTERN = /\{ov:(\d+)\}/g;

function label(id) {
    return "ov:" + (id || "?");
}

// Toolbar icon — stacked layers (an overlay = a layer on the map); stroke:currentColor like the link icon.
const OVERLAY_ICON = '<path d="M12 3l9 5-9 5-9-5 9-5z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>'
    + '<path d="M3 13l9 5 9-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>';

// German fallbacks only — the consumer passes translated labels (infoTextEditor.js).
const DEFAULT_LABELS = { title: "Overlay-Link", button: "Overlay", idField: "Overlay-ID:", tokenTitle: "Overlay" };

// options.picker (optional, injected at registration): (applyValues) => HTMLElement — the popover overlay list.
// Kept out of this module so the entity stays pure (no DOM/IO); the wiring provides it (get_overlays-backed).
// options.labels (optional): translated texts, see DEFAULT_LABELS.
export function overlayLinkEntity(options = {}) {
    const text = { ...DEFAULT_LABELS, ...options.labels };
    return {
        kind: "overlaylink",
        pattern: OVERLAY_PATTERN,
        parse: (m) => ({ id: m[1] }),
        renderHtml: (data, idx) =>
            '<span class="topo2-link--intern topo2-editor-ovlink" data-kind="overlaylink" data-idx="' + idx +
            '" data-ov-id="' + escapeHtml(data.id || "") + '" title="' + escapeHtml(text.tokenTitle + " " + (data.id || "")) + '">' +
            escapeHtml(label(data.id)) + "</span>",
        // ── Nabe edges ──
        serialize: (data) => "{ov:" + (data.id ?? "") + "}",
        // Frontend: inert internal overlay link (the visible+center activation is wired by the consumer later).
        // topo2-link--intern (accent text, hover underline), NOT base topo2-link → no external icon. Carries
        // data-ov-id for the click handler.
        renderFrontendHtml: (data) =>
            '<a class="topo2-link--intern topo2-ovlink" data-ov-id="' + escapeHtml(data.id || "") + '">' +
            escapeHtml(label(data.id)) + "</a>",
        // Contenteditable form: an ATOMIC token (contenteditable=false) — the label is derived from the id, so
        // it is not free-text-editable; edit/remove happen via the popover on click.
        renderLiveHtml: (data) =>
            '<a class="topo2-link--intern topo2-editor-ovlink topo2-live-ovlink" data-kind="overlaylink" contenteditable="false" data-ov-id="' +
            escapeHtml(data.id || "") + '" title="' + escapeHtml(text.tokenTitle + " " + (data.id || "")) + '">' +
            escapeHtml(label(data.id)) + "</a>",
        matchElement: (el) =>
            (el.classList && el.classList.contains("topo2-live-ovlink"))
                ? { id: (el.dataset && el.dataset.ovId) || "" }
                : null,
        edit: {
            title: text.title,
            label: text.button,                                    // selection-toolbar button (fallback text)
            icon: OVERLAY_ICON,                                    // preferred: square icon button (like link)
            fields: [{ key: "id", label: text.idField }],         // popover shows the id; the picker list renders below it
            canCreate: (selectedText) => /^\d+$/.test((selectedText || "").trim()),  // numeric selection → button appears (next to "Doc")
            newData: (selectedText) => ({ id: (selectedText || "").trim() }),         // the selection is a candidate id
            // NOT silentCreate (unlike docLink): the popover opens so the user can confirm the id OR pick from the
            // overlay list. `picker` is INJECTED at registration → keeps this module pure (no DOM/IO here):
            //   picker(applyValues) => HTMLElement   — the list element; on select it calls applyValues({ id }).
            picker: options.picker || null,
            normalize: (values) => ({ id: (values.id || "").trim() }),
            format: (data) => "{ov:" + data.id + "}",
            remove: (data) => data.id,   // unwrap: {ov:id} → the bare id number as plain text (like docLink/link)
        },
    };
}
